(() => {
    class QuickOrder {
        constructor(section) {
            this.section = section;

            this.configElement = section.querySelector(
                '[data-quick-order-config]'
            );

            if (!this.configElement) {
                console.warn(
                    'Quick Order: configuration not found.'
                );
                return;
            }

            try {
                this.config = JSON.parse(
                    this.configElement.textContent
                );
            } catch (error) {
                console.error(
                    'Quick Order: unable to parse configuration.',
                    error
                );
                return;
            }

            /*
             * Search
             */
            this.searchInput = section.querySelector(
                '[data-quick-order-search]'
            );

            this.searchStatus = section.querySelector(
                '[data-quick-order-search-status]'
            );

            /*
             * Collection accordion
             */
            this.collectionDropdown = section.querySelector(
                '[data-quick-order-collection-dropdown]'
            );

            this.collectionTrigger = section.querySelector(
                '[data-quick-order-collection-trigger]'
            );

            this.collectionOptions = section.querySelector(
                '[data-quick-order-collection-options]'
            );

            this.collectionLabel = section.querySelector(
                '[data-quick-order-collection-label]'
            );

            /*
             * Product results
             */
            this.productsContainer = section.querySelector(
                '[data-quick-order-products]'
            );

            this.loadingElement = section.querySelector(
                '[data-quick-order-loading]'
            );

            this.emptyElement = section.querySelector(
                '[data-quick-order-empty]'
            );

            this.loadMoreButton = section.querySelector(
                '[data-quick-order-load-more]'
            );

            /*
             * Your Order
             */
            this.orderItems = section.querySelector(
                '[data-quick-order-order-items]'
            );

            this.orderEmpty = section.querySelector(
                '[data-quick-order-order-empty]'
            );

            this.orderSummary = section.querySelector(
                '[data-quick-order-summary]'
            );

            this.unitCount = section.querySelector(
                '[data-quick-order-unit-count]'
            );

            this.subtotal = section.querySelector(
                '[data-quick-order-subtotal]'
            );

            this.addOrderButton = section.querySelector(
                '[data-quick-order-add]'
            );

            this.clearButton = section.querySelector(
                '[data-quick-order-clear]'
            );

            this.messageElement = section.querySelector(
                '[data-quick-order-message]'
            );

            /*
             * Product/loading state
             */
            this.products = [];

            this.currentCollection = '';
            this.currentPage = 1;
            this.hasMoreProducts = false;
            this.isLoading = false;
            this.searchQuery = '';

            /*
             * Working order
             */
            this.order = new Map();

            /*
             * Prevent duplicate cart submissions
             */
            this.isAddingToCart = false;

            /*
             * BARCODE SCANNER STATE
             *
             * These are intentionally here for the later
             * barcode phase. No scanner behavior is being
             * initialized yet.
             */
            this.barcodeBuffer = '';
            this.barcodeLastKeyTime = 0;
            this.barcodeResetTimer = null;
            this.barcodeLookupInProgress = false;

            this.init();
        }

        init() {
            this.bindEvents();
            this.restoreOrder();
            this.renderOrder();

            /*
             * We intentionally do NOT automatically load
             * products when the page first opens.
             *
             * Products are displayed after:
             * 1. Selecting a collection, or
             * 2. Searching.
             */
        }

        bindEvents() {
            /*
             * Search
             */
            if (this.searchInput) {
                this.searchInput.addEventListener(
                    'input',
                    this.debounce((event) => {
                        this.handleSearch(
                            event.target.value
                        );
                    }, 300)
                );
            }

            /*
             * Collection accordion trigger
             */
            if (
                this.collectionTrigger &&
                this.collectionOptions
            ) {
                this.collectionTrigger.addEventListener(
                    'click',
                    () => {
                        const isOpen =
                            this.collectionTrigger.getAttribute(
                                'aria-expanded'
                            ) === 'true';

                        this.setCollectionDropdownState(
                            !isOpen
                        );
                    }
                );
            }

            /*
             * Collection options
             */
            this.section
                .querySelectorAll(
                    '[data-quick-order-collection]'
                )
                .forEach((option) => {
                    option.addEventListener(
                        'click',
                        () => {
                            const handle =
                                option.dataset
                                    .quickOrderCollection;

                            if (!handle) {
                                return;
                            }

                            /*
                             * Change the accordion label to
                             * the selected collection title.
                             */
                            if (this.collectionLabel) {
                                this.collectionLabel.textContent =
                                    option.textContent.trim();
                            }

                            /*
                             * Mark the selected option active.
                             */
                            this.section
                                .querySelectorAll(
                                    '[data-quick-order-collection]'
                                )
                                .forEach(
                                    (
                                        collectionOption
                                    ) => {
                                        const isActive =
                                            collectionOption ===
                                            option;

                                        collectionOption.classList.toggle(
                                            'is-active',
                                            isActive
                                        );

                                        collectionOption.setAttribute(
                                            'aria-pressed',
                                            String(
                                                isActive
                                            )
                                        );
                                    }
                                );

                            /*
                             * Close accordion after choosing
                             * a collection.
                             */
                            this.setCollectionDropdownState(
                                false
                            );

                            /*
                             * Use our existing product-loading
                             * behavior.
                             */
                            this.handleCollectionChange(
                                handle
                            );
                        }
                    );
                });

            /*
             * Load more
             */
            if (this.loadMoreButton) {
                this.loadMoreButton.addEventListener(
                    'click',
                    () => {
                        if (this.isLoading) {
                            return;
                        }

                        this.currentPage += 1;

                        this.loadProducts({
                            append: true
                        });
                    }
                );
            }

            /*
             * Clear order
             */
            if (this.clearButton) {
                this.clearButton.addEventListener(
                    'click',
                    () => {
                        this.clearOrder();
                    }
                );
            }

            /*
             * Add entire order to Shopify cart
             */
            if (this.addOrderButton) {
                this.addOrderButton.addEventListener(
                    'click',
                    () => {
                        this.addOrderToCart();
                    }
                );
            }
        }


        handleSearch(value) {
            this.searchQuery = value.trim();

            /*
             * If the search field is cleared,
             * clear the search results.
             */
            if (!this.searchQuery) {
                this.products = [];

                if (this.searchStatus) {
                    this.searchStatus.textContent = '';
                }

                if (this.productsContainer) {
                    this.productsContainer.innerHTML = '';
                }

                if (this.emptyElement) {
                    this.emptyElement.hidden = true;
                }

                if (this.loadMoreButton) {
                    this.loadMoreButton.hidden = true;
                }

                return;
            }

            this.searchProducts(
                this.searchQuery
            );
        }

        async searchProducts(query) {
            if (!query) {
                return;
            }

            if (this.loadingElement) {
                this.loadingElement.hidden = false;
            }

            if (this.emptyElement) {
                this.emptyElement.hidden = true;
            }

            /*
             * Load More only applies to collection
             * pagination, not predictive search.
             */
            if (this.loadMoreButton) {
                this.loadMoreButton.hidden = true;
            }

            try {
                const url =
                    `/search/suggest.json?q=${encodeURIComponent(
                        query
                    )}` +
                    `&resources[type]=product` +
                    `&resources[limit]=10` +
                    `&resources[options][unavailable_products]=show`;

                const response = await fetch(
                    url,
                    {
                        headers: {
                            Accept:
                                'application/json'
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Search request failed: ${response.status}`
                    );
                }

                const data =
                    await response.json();

                const results =
                    data.resources?.results
                        ?.products || [];

                /*
                 * Predictive search gives us product
                 * results, but we want the complete
                 * product JSON so we have variants,
                 * SKUs and availability.
                 */
                const products =
                    await Promise.all(
                        results.map(
                            async (result) => {
                                try {
                                    const productUrl =
                                        new URL(
                                            result.url,
                                            window
                                                .location
                                                .origin
                                        );

                                    const pathParts =
                                        productUrl.pathname
                                            .split('/')
                                            .filter(
                                                Boolean
                                            );

                                    const productsIndex =
                                        pathParts.indexOf(
                                            'products'
                                        );

                                    if (
                                        productsIndex ===
                                        -1 ||
                                        !pathParts[
                                        productsIndex +
                                        1
                                        ]
                                    ) {
                                        console.warn(
                                            'Quick Order: unable to determine product handle:',
                                            result
                                        );

                                        return null;
                                    }

                                    const handle =
                                        pathParts[
                                        productsIndex +
                                        1
                                        ];

                                    const productResponse =
                                        await fetch(
                                            `/products/${encodeURIComponent(
                                                handle
                                            )}.js`,
                                            {
                                                headers:
                                                {
                                                    Accept:
                                                        'application/json'
                                                }
                                            }
                                        );

                                    if (
                                        !productResponse.ok
                                    ) {
                                        console.warn(
                                            'Quick Order: product request failed:',
                                            handle,
                                            productResponse.status
                                        );

                                        return null;
                                    }

                                    return await productResponse.json();
                                } catch (
                                error
                                ) {
                                    console.warn(
                                        'Quick Order: unable to retrieve product:',
                                        result.url,
                                        error
                                    );

                                    return null;
                                }
                            }
                        )
                    );

                /*
                 * Remove products that failed to load.
                 */
                this.products =
                    products.filter(Boolean);

                /*
                 * Search status.
                 *
                 * This is intentionally present only
                 * once; the duplicate block from the
                 * previous file has been removed.
                 */
                if (this.searchStatus) {
                    const count =
                        this.products.length;

                    this.searchStatus.textContent =
                        count === 1
                            ? '1 product found'
                            : `${count} products found`;
                }

                if (
                    this.productsContainer
                ) {
                    this.productsContainer.innerHTML =
                        '';
                }

                this.renderProducts(
                    this.products
                );

                if (
                    this.products.length ===
                    0 &&
                    this.emptyElement
                ) {
                    this.emptyElement.hidden =
                        false;
                }
            } catch (error) {
                console.error(
                    'Quick Order search error:',
                    error
                );

                this.showMessage(
                    'Unable to search products. Please try again.'
                );
            } finally {
                if (this.loadingElement) {
                    this.loadingElement.hidden =
                        true;
                }
            }
        }

        /*
         * Open or close the collection accordion.
         */
        setCollectionDropdownState(
            isOpen
        ) {
            if (this.collectionTrigger) {
                this.collectionTrigger.setAttribute(
                    'aria-expanded',
                    String(isOpen)
                );
            }

            if (this.collectionOptions) {
                this.collectionOptions.hidden =
                    !isOpen;
            }
        }

        handleCollectionChange(handle) {
            this.currentCollection = handle;
            this.currentPage = 1;
            this.products = [];
            this.searchQuery = '';

            /*
             * Selecting a collection clears any
             * previous text search.
             */
            if (this.searchInput) {
                this.searchInput.value = '';
            }

            if (this.searchStatus) {
                this.searchStatus.textContent = '';
            }

            this.loadProducts();
        }

        async loadProducts({
            append = false
        } = {}) {
            if (this.isLoading) {
                return;
            }

            this.isLoading = true;

            if (this.loadingElement) {
                this.loadingElement.hidden = false;
            }

            if (this.emptyElement) {
                this.emptyElement.hidden = true;
            }

            /*
             * Hide Load More while the request
             * itself is running.
             */
            if (this.loadMoreButton) {
                this.loadMoreButton.hidden = true;
            }

            if (!append) {
                this.products = [];

                if (
                    this.productsContainer
                ) {
                    this.productsContainer.innerHTML =
                        '';
                }
            }

            try {
                const products =
                    await this.fetchProducts();

                if (append) {
                    this.products.push(
                        ...products
                    );
                } else {
                    this.products =
                        products;
                }

                this.hasMoreProducts =
                    products.length ===
                    Number(
                        this.config
                            .productsPerPage
                    );

                this.renderProducts(
                    products,
                    {
                        append
                    }
                );

                if (
                    !append &&
                    this.products.length ===
                    0 &&
                    this.emptyElement
                ) {
                    this.emptyElement.hidden =
                        false;
                }

                if (
                    this.loadMoreButton
                ) {
                    this.loadMoreButton.hidden =
                        !this.hasMoreProducts;
                }
            } catch (error) {
                console.error(
                    'Quick Order: unable to load products.',
                    error
                );

                /*
                 * Make sure Load More remains
                 * hidden after an error.
                 */
                if (
                    this.loadMoreButton
                ) {
                    this.loadMoreButton.hidden =
                        true;
                }

                this.showMessage(
                    'Unable to load products. Please try again.'
                );
            } finally {
                this.isLoading = false;

                if (this.loadingElement) {
                    this.loadingElement.hidden =
                        true;
                }
            }
        }

        async fetchProducts() {
            const limit =
                Number(
                    this.config
                        .productsPerPage
                ) || 20;

            if (
                !this.currentCollection
            ) {
                return [];
            }

            const url =
                `/collections/${encodeURIComponent(
                    this.currentCollection
                )}/products.json?limit=${limit}&page=${this.currentPage}`;

            const response =
                await fetch(
                    url,
                    {
                        headers: {
                            Accept:
                                'application/json'
                        }
                    }
                );

            if (!response.ok) {
                throw new Error(
                    `Product request failed: ${response.status}`
                );
            }

            const data =
                await response.json();

            return data.products || [];
        }

        renderProducts(
            products,
            { append = false } = {}
        ) {
            if (!this.productsContainer) return;

            const template =
                document.getElementById(
                    `QuickOrderProductTemplate-${this.config.sectionId}`
                );

            if (!template) {
                console.error(
                    'Quick Order: product template not found.'
                );
                return;
            }

            if (!append) {
                this.productsContainer.innerHTML = '';
            }

            const fragment =
                document.createDocumentFragment();

            products.forEach((product) => {
                if (!Array.isArray(product.variants)) {
                    return;
                }

                product.variants.forEach((variant) => {
                    const row =
                        template.content.firstElementChild.cloneNode(
                            true
                        );

                    row.dataset.productId =
                        product.id;

                    row.dataset.variantId =
                        variant.id;

                    row.dataset.barcode =
                        variant.barcode || '';

                    const image =
                        row.querySelector(
                            '[data-product-image]'
                        );

                    const title =
                        row.querySelector(
                            '[data-product-title]'
                        );

                    const variantTitle =
                        row.querySelector(
                            '[data-variant-title]'
                        );

                    const sku =
                        row.querySelector(
                            '[data-product-sku]'
                        );

                    const price =
                        row.querySelector(
                            '[data-product-price]'
                        );

                    const quantity =
                        row.querySelector(
                            '[data-product-quantity]'
                        );

                    const addButton =
                        row.querySelector(
                            '[data-product-add]'
                        );

                    if (title) {
                        title.textContent =
                            product.title;
                    }

                    if (
                        variantTitle &&
                        variant.title &&
                        variant.title !== 'Default Title'
                    ) {
                        variantTitle.textContent =
                            variant.title;

                        variantTitle.hidden = false;
                    }

                    if (sku) {
                        sku.textContent =
                            variant.sku || '—';
                    }

                    const priceCents =
                        Number.isInteger(variant.price)
                            ? variant.price
                            : Math.round(
                                Number.parseFloat(
                                    variant.price || 0
                                ) * 100
                            );

                    if (price) {
                        // Show the normal Shopify price first.
                        // WPD will replace this automatically for eligible wholesale customers.
                        price.textContent =
                            this.formatMoney(
                                priceCents
                            );

                        if (
                            this.config.isWholesaleCustomer &&
                            product.handle &&
                            variant.id
                        ) {
                            price.setAttribute(
                                'data-wpd-product-handle',
                                product.handle
                            );

                            price.setAttribute(
                                'data-wpd-variant-id',
                                String(variant.id)
                            );

                            price.setAttribute(
                                'data-wpd-variant-price',
                                String(priceCents)
                            );

                            price.setAttribute(
                                'data-wpd-variant-compare-at-price',
                                variant.compare_at_price
                                    ? String(
                                        Number.isInteger(
                                            variant.compare_at_price
                                        )
                                            ? variant.compare_at_price
                                            : Math.round(
                                                Number.parseFloat(
                                                    variant.compare_at_price
                                                ) * 100
                                            )
                                    )
                                    : ''
                            );
                        }
                    }

                    if (quantity) {
                        const minimum =
                            Number(
                                this.config.minimumQuantity
                            ) || 1;

                        quantity.min = minimum;
                        quantity.value = minimum;
                    }

                    const imageUrl =
                        typeof product.featured_image === 'string'
                            ? product.featured_image
                            : product.featured_image?.src ||
                            product.image?.src ||
                            (typeof product.images?.[0] === 'string'
                                ? product.images[0]
                                : product.images?.[0]?.src) ||
                            '';

                    if (image) {
                        if (imageUrl) {
                            image.src = imageUrl;
                            image.alt =
                                product.title || '';
                        } else {
                            image.hidden = true;
                        }
                    }

                    if (!variant.available) {
                        row.classList.add(
                            'quick-order-product--sold-out'
                        );

                        if (quantity) {
                            quantity.disabled = true;
                        }

                        if (addButton) {
                            addButton.disabled = true;
                            addButton.textContent =
                                'Sold Out';
                        }
                    } else if (addButton) {
                        addButton.addEventListener(
                            'click',
                            () => {
                                let effectivePriceCents =
                                    priceCents;

                                /*
                                 * If WPD has processed this price,
                                 * use the price currently displayed
                                 * by WPD.
                                 */
                                if (
                                    price &&
                                    price.classList.contains(
                                        'wpd-processed'
                                    )
                                ) {
                                    const priceText =
                                        price.textContent.trim();

                                    const numericPrice =
                                        Number.parseFloat(
                                            priceText
                                                .replace(/[^0-9.]/g, '')
                                        );

                                    if (
                                        Number.isFinite(
                                            numericPrice
                                        )
                                    ) {
                                        effectivePriceCents =
                                            Math.round(
                                                numericPrice * 100
                                            );
                                    }
                                }

                                this.addProductToOrder({
                                    product,
                                    variant,
                                    priceCents:
                                        effectivePriceCents,
                                    quantity:
                                        Number.parseInt(
                                            quantity?.value,
                                            10
                                        )
                                });
                            }
                        );
                    }

                    fragment.appendChild(row);
                });
            });

            this.productsContainer.appendChild(
                fragment
            );
        }

        addProductToOrder({
            product,
            variant,
            priceCents,
            quantity
        }) {
            const minimum =
                Number(
                    this.config.minimumQuantity
                ) || 1;

            let requestedQuantity =
                Number(quantity);

            if (
                !Number.isInteger(
                    requestedQuantity
                ) ||
                requestedQuantity < minimum
            ) {
                requestedQuantity = minimum;
            }

            const key =
                String(variant.id);

            const existing =
                this.order.get(key);

            if (existing) {
                existing.quantity +=
                    requestedQuantity;

                /*
                 * Refresh the stored price with the
                 * currently processed WPD/Shopify price.
                 */
                existing.price =
                    priceCents;

                this.order.set(
                    key,
                    existing
                );
            } else {
                this.order.set(
                    key,
                    {
                        variantId: variant.id,
                        productId: product.id,
                        title: product.title,
                        variantTitle:
                            variant.title ===
                                'Default Title'
                                ? ''
                                : variant.title,
                        sku: variant.sku || '',
                        barcode:
                            variant.barcode || '',
                        quantity:
                            requestedQuantity,
                        price: priceCents
                    }
                );
            }

            this.saveOrder();
            this.renderOrder();

            this.showMessage(
                `${product.title} added to your order.`
            );
        }

        renderOrder() {
            if (!this.orderItems) {
                return;
            }

            this.orderItems.innerHTML = '';

            let totalUnits = 0;
            let subtotalCents = 0;

            // EMPTY ORDER
            if (this.order.size === 0) {
                if (this.orderEmpty) {
                    this.orderEmpty.hidden = false;
                }

                if (this.orderSummary) {
                    this.orderSummary.hidden = true;
                }

                if (this.unitCount) {
                    this.unitCount.textContent = '0';
                }

                if (this.subtotal) {
                    this.subtotal.textContent =
                        this.formatMoney(0);
                }

                if (this.addOrderButton) {
                    this.addOrderButton.disabled = true;
                }

                return;
            }

            // ORDER HAS ITEMS
            if (this.orderEmpty) {
                this.orderEmpty.hidden = true;
            }

            if (this.orderSummary) {
                this.orderSummary.hidden = false;
            }

            const minimumQuantity =
                this.config.formType === 'wholesale'
                    ? Number(this.config.minimumQuantity) || 1
                    : 1;

            this.order.forEach((item, key) => {
                totalUnits += item.quantity;

                subtotalCents +=
                    item.price * item.quantity;

                const row =
                    document.createElement('div');

                row.className =
                    'quick-order__order-item';

                row.dataset.variantId =
                    item.variantId;

                row.innerHTML = `
            <div class="quick-order__order-item-info">

                <h4 class="quick-order__order-item-title">
                    ${this.escapeHtml(item.title)}
                </h4>

                ${item.variantTitle &&
                        item.variantTitle !== 'Default Title'
                        ? `
                            <span class="quick-order__order-item-variant">
                                ${this.escapeHtml(
                            item.variantTitle
                        )}
                            </span>
                        `
                        : ''
                    }

                ${item.sku
                        ? `
                            <span class="quick-order__order-sku">
                                SKU:
                                ${this.escapeHtml(
                            item.sku
                        )}
                            </span>
                        `
                        : ''
                    }

                <span class="quick-order__order-unit-price">
                    ${this.formatMoney(
                        item.price
                    )} each
                </span>

            </div>

            <div class="quick-order__order-item-actions">

                <label class="quick-order__order-quantity-wrapper">

                    <span class="visually-hidden">
                        Quantity for
                        ${this.escapeHtml(
                        item.title
                    )}
                    </span>
                    <br />
                    <input
                        type="number"
                        min="1"
                        step="1"
                        value="${item.quantity}"
                        class="quick-order__order-quantity"
                        data-order-quantity
                    >

                </label>

                <span class="quick-order__order-line-total">
                    ${this.formatMoney(
                        item.price *
                        item.quantity
                    )}
                </span>

                <button
                    type="button"
                    class="quick-order__order-remove main-btn-2"
                    data-order-remove
                >
                    Remove
                </button>

            </div>
        `;

                const quantityInput =
                    row.querySelector(
                        '[data-order-quantity]'
                    );

                const removeButton =
                    row.querySelector(
                        '[data-order-remove]'
                    );

                quantityInput?.addEventListener(
                    'change',
                    () => {
                        this.updateOrderQuantity(
                            key,
                            quantityInput.value
                        );
                    }
                );

                removeButton?.addEventListener(
                    'click',
                    () => {
                        this.removeOrderItem(key);
                    }
                );

                this.orderItems.appendChild(row);
            });

            // UPDATE SUMMARY
            if (this.unitCount) {
                this.unitCount.textContent =
                    String(totalUnits);
            }

            if (this.subtotal) {
                this.subtotal.textContent =
                    this.formatMoney(
                        subtotalCents
                    );
            }

            if (this.addOrderButton) {
                this.addOrderButton.disabled = false;
            }
        }

        updateOrderQuantity(key, quantity) {
            const item = this.order.get(key);

            if (!item) {
                return;
            }

            let newQuantity =
                Number.parseInt(quantity, 10);

            if (
                !Number.isFinite(newQuantity) ||
                newQuantity < 1
            ) {
                newQuantity = 1;
            }

            item.quantity = newQuantity;

            this.order.set(key, item);

            this.saveOrder();
            this.renderOrder();
        }

        removeOrderItem(key) {
            if (!this.order.has(key)) {
                return;
            }

            this.order.delete(key);

            this.saveOrder();
            this.renderOrder();

            this.showMessage(
                'Product removed from your order.'
            );
        }

        clearOrder() {
            this.order.clear();

            this.saveOrder();
            this.renderOrder();

            this.showMessage(
                'Order cleared.'
            );
        }

        async addOrderToCart() {
            // Prevent duplicate submissions
            if (this.isAddingToCart) {
                return;
            }
            if (this.order.size === 0) {
                this.showMessage(
                    'Add at least one product first.'
                );

                return;
            }

            if (!this.addOrderButton) {
                return;
            }

            this.isAddingToCart = true;

            const originalButtonText =
                this.addOrderButton.textContent;

            this.addOrderButton.disabled = true;
            this.addOrderButton.textContent =
                'Adding to cart...';

            try {
                /*
                 * Shopify Ajax Cart accepts multiple
                 * line items in a single request.
                 *
                 * IMPORTANT:
                 * We send only the variant ID and quantity.
                 * WPD remains responsible for wholesale pricing.
                 */
                const items =
                    Array.from(
                        this.order.values()
                    ).map((item) => ({
                        id: item.variantId,
                        quantity: item.quantity
                    }));

                // console.log(
                //     'Quick Order adding to cart:',
                //     items
                // );

                const response = await fetch(
                    `${window.Shopify?.routes?.root || '/'}cart/add.js`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type':
                                'application/json',
                            Accept:
                                'application/json'
                        },
                        body: JSON.stringify({
                            items
                        })
                    }
                );

                const result =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.description ||
                        result.message ||
                        'Unable to add order to cart.'
                    );
                }

                /*
                 * Get the updated Shopify cart.
                 */
                const cartResponse =
                    await fetch(
                        `${window.Shopify?.routes?.root || '/'}cart.js`,
                        {
                            headers: {
                                Accept:
                                    'application/json'
                            }
                        }
                    );

                if (!cartResponse.ok) {
                    throw new Error(
                        'Order was added, but the cart could not be refreshed.'
                    );
                }

                const cart =
                    await cartResponse.json();

                // console.log(
                //     'Quick Order updated cart:',
                //     cart
                // );

                /*
                 * Clear the temporary Quick Order only
                 * after Shopify confirms the add.
                 */
                this.order.clear();
                this.saveOrder();
                this.renderOrder();

                document.dispatchEvent(
                    new CustomEvent(
                        'quick-order:cart-updated',
                        {
                            detail: {
                                cart
                            }
                        }
                    )
                );

                if (this.config.isWholesaleCustomer) {
                    this.showMessage(
                        'Order added. Opening your wholesale cart...'
                    );

                    this.addOrderButton.textContent =
                        'Opening wholesale cart...';

                    window.setTimeout(() => {
                        window.location.href =
                            '/apps/wpdapp/wholesale/wpd-cart';
                    }, 500);

                    return;
                }

                this.showMessage(
                    `${cart.item_count} item${cart.item_count === 1
                        ? ''
                        : 's'
                    } currently in your cart.`
                );

                this.addOrderButton.textContent =
                    'Added to cart';

                window.setTimeout(() => {
                    this.addOrderButton.textContent =
                        originalButtonText;
                }, 1500);

            } catch (error) {
                console.error(
                    'Quick Order cart error:',
                    error
                );

                this.showMessage(
                    error.message ||
                    'Unable to add order to cart.'
                );

                this.addOrderButton.textContent =
                    originalButtonText;

                this.isAddingToCart = false;
                this.addOrderButton.disabled = false;

            } finally {
                if (!this.config.isWholesaleCustomer) {
                    this.isAddingToCart = false;
                    this.addOrderButton.disabled = false;
                }
            }
        }

        saveOrder() {
            try {
                sessionStorage.setItem(
                    this.storageKey,
                    JSON.stringify(
                        Array.from(
                            this.order.entries()
                        )
                    )
                );
            } catch (error) {
                console.warn(
                    'Quick Order: unable to save order.',
                    error
                );
            }
        }

        restoreOrder() {
            try {
                const saved =
                    sessionStorage.getItem(
                        this.storageKey
                    );

                if (!saved) return;

                this.order =
                    new Map(
                        JSON.parse(saved)
                    );
            } catch (error) {
                console.warn(
                    'Quick Order: unable to restore order.',
                    error
                );
            }
        }

        get storageKey() {
            return `quick-order-${this.config.formType}-${this.config.sectionId}`;
        }

        showMessage(message) {
            if (!this.messageElement) {
                return;
            }

            this.messageElement.textContent =
                message;
        }

        escapeHtml(value) {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
        }

        formatMoney(cents) {
            return new Intl.NumberFormat(
                document.documentElement.lang ||
                'en-US',
                {
                    style: 'currency',
                    currency: 'USD'
                }
            ).format(cents / 100);
        }

        debounce(
            callback,
            delay = 300
        ) {
            let timeout;

            return (...args) => {
                clearTimeout(timeout);

                timeout =
                    setTimeout(() => {
                        callback(...args);
                    }, delay);
            };
        }
    }

    function initializeQuickOrders(
        container = document
    ) {
        /*
         * If container itself is the quick-order
         * section (Theme Editor reload), include it.
         */
        const sections = [];

        if (
            container.matches?.(
                '[data-quick-order]'
            )
        ) {
            sections.push(container);
        }

        container
            .querySelectorAll?.(
                '[data-quick-order]'
            )
            .forEach((section) => {
                sections.push(section);
            });

        sections.forEach((section) => {
            if (
                section.dataset
                    .quickOrderInitialized
            ) {
                return;
            }

            section.dataset
                .quickOrderInitialized =
                'true';

            new QuickOrder(section);
        });
    }

    document.addEventListener(
        'DOMContentLoaded',
        () => {
            initializeQuickOrders();
        }
    );

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            initializeQuickOrders(
                event.target
            );
        }
    );
})();